"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.APosInventoryScanStatusSchema = exports.APosInventorySuppressionSchema = exports.APosInventoryWatchlistEntrySchema = exports.APosInventoryAlertSettingsSchema = exports.APosInventoryAlertSchema = exports.APosInventoryForecastEnvelopeSchema = exports.APosXyzClassSchema = exports.APosAbcClassSchema = exports.APosReorderOptionSchema = exports.APosTransferOptionSchema = exports.APosRecommendedActionSchema = exports.APosForecastConfidenceSchema = void 0;
// inventory-alert.schema.ts
const zod_1 = require("zod");
const id_schema_1 = require("../../common/id.schema");
exports.APosForecastConfidenceSchema = zod_1.z.enum(['low', 'medium', 'high']);
exports.APosRecommendedActionSchema = zod_1.z.enum(['transfer', 'reorder', 'consider_stocking']);
exports.APosTransferOptionSchema = zod_1.z.object({
    fromLocationId: zod_1.z.string().min(1),
    qty: zod_1.z.number().int().positive(),
});
exports.APosReorderOptionSchema = zod_1.z.object({
    supplierId: zod_1.z.string().nullable(),
    qty: zod_1.z.number().int().positive(),
    unitCost: zod_1.z.number().nonnegative().optional(),
});
exports.APosAbcClassSchema = zod_1.z.enum(['A', 'B', 'C']);
exports.APosXyzClassSchema = zod_1.z.enum(['X', 'Y', 'Z']);
exports.APosInventoryForecastEnvelopeSchema = zod_1.z.object({
    forecastQty: zod_1.z.number().nonnegative(),
    confidence: exports.APosForecastConfidenceSchema,
    transferOption: exports.APosTransferOptionSchema.nullable(),
    reorderOption: exports.APosReorderOptionSchema.nullable(),
    recommendedAction: exports.APosRecommendedActionSchema,
    explanation: zod_1.z.string().min(1).max(1000),
});
// Stored as a row in APosAgentSuggestionsTable (pk tenantId, sk suggestionId).
// status drives StatusIndex; targetKey (`variant#location`) drives TargetIndex.
exports.APosInventoryAlertSchema = zod_1.z.object({
    tenantId: id_schema_1.TenantIdSchema,
    suggestionId: zod_1.z.string().min(1),
    kind: zod_1.z.enum(['restock', 'consider_stocking']).default('restock'),
    variantId: zod_1.z.string().optional(), // absent for consider_stocking (not yet a variant)
    productId: zod_1.z.string().optional(),
    locationId: zod_1.z.string().min(1),
    displayName: zod_1.z.string().optional(),
    // Product Intelligence (2026-10-11): resolved at scan time so the page and
    // the digest show a real name + picture without a per-product lookup.
    productName: zod_1.z.string().optional(),
    thumbnail: zod_1.z.string().optional(),
    sku: zod_1.z.string().optional(),
    onHand: zod_1.z.number().optional(),
    reorderPoint: zod_1.z.number().optional(),
    avgDailySales: zod_1.z.number().optional(),
    avgDailySales30d: zod_1.z.number().optional(), // 30-day average daily sales (velocity context)
    soldLast30Days: zod_1.z.number().optional(), // total units sold in last 30 days (≈ avgDailySales30d * 30)
    daysLeft: zod_1.z.number().optional(),
    severity: zod_1.z.enum(['low', 'medium', 'high']).default('medium'),
    status: zod_1.z.enum(['new', 'approved', 'dismissed']).default('new'),
    targetKey: zod_1.z.string().optional(), // `${variantId}#${locationId}`
    runDate: zod_1.z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    forecast: exports.APosInventoryForecastEnvelopeSchema.optional(),
    openRequestCount: zod_1.z.number().int().nonnegative().default(0),
    openRequestTypes: zod_1.z.array(zod_1.z.string()).default([]),
    // approve/dismiss outcome (learning loop)
    linkedDraftType: zod_1.z.enum(['transfer', 'po']).nullable().default(null),
    linkedDraftId: zod_1.z.string().nullable().default(null),
    dismissReason: zod_1.z.string().optional(),
    stockedOutBeforeRestock: zod_1.z.boolean().optional(),
    createdAt: zod_1.z.number().int().positive(),
    updatedAt: zod_1.z.number().int().positive().optional(),
    ttl: zod_1.z.number().int().positive().optional(),
    perLocationQty: zod_1.z.record(zod_1.z.string(), zod_1.z.number()).optional(),
    // — Replenishment engine enrichment (additive; all optional) —
    abcClass: exports.APosAbcClassSchema.optional(),
    xyzClass: exports.APosXyzClassSchema.optional(),
    inventoryPosition: zod_1.z.number().optional(),
    onOrder: zod_1.z.number().optional(),
    reorderPointComputed: zod_1.z.number().optional(),
    recommendedOrderQty: zod_1.z.number().optional(),
    projectedStockoutDate: zod_1.z.string().optional(), // YYYY-MM-DD
});
// Stored in APosAgentAlertsTable: pk=`TENANT#${tenantId}`, sk=`ALERT_CONFIG#${locationId|'DEFAULT'}`
exports.APosInventoryAlertSettingsSchema = zod_1.z.object({
    triggerMethod: zod_1.z.enum(['days_of_cover', 'reorder', 'both']).default('both'),
    safetyDays: zod_1.z.number().int().nonnegative().default(3),
    cadence: zod_1.z.enum(['daily', 'realtime', 'both']).default('daily'),
    globalLeadTimeDays: zod_1.z.number().int().positive().default(7),
    accentTheme: zod_1.z.enum(['indigo', 'violet', 'ruby', 'emerald', 'blue', 'slate', 'amber', 'custom']).default('indigo'),
    accentColorHex: zod_1.z.string().regex(/^#([0-9a-fA-F]{6})$/).optional(),
    // — Replenishment engine (additive; all optional w/ safe defaults) —
    minVelocity: zod_1.z.number().nonnegative().optional(),
    whatIfReorderQty: zod_1.z.number().int().nonnegative().optional(),
    perStoreLeadTimeDays: zod_1.z.record(zod_1.z.string(), zod_1.z.number().int().positive()).optional(),
    selectedCategoryIds: zod_1.z.array(zod_1.z.string()).optional(),
    /** Categories Ruby must skip ("all except these"). Product Intelligence, 2026-10-11. */
    excludedCategoryIds: zod_1.z.array(zod_1.z.string()).optional(),
    serviceLevelByClass: zod_1.z.object({
        A: zod_1.z.number().min(0).max(1), B: zod_1.z.number().min(0).max(1), C: zod_1.z.number().min(0).max(1),
    }).partial().optional(),
    abcThresholds: zod_1.z.object({ a: zod_1.z.number().min(0).max(1), b: zod_1.z.number().min(0).max(1) }).optional(),
    reviewPeriodDays: zod_1.z.number().int().positive().optional(),
    recentGrnDays: zod_1.z.number().int().nonnegative().default(7),
    replenishmentMode: zod_1.z.enum(['auto', 'single', 'multi']).default('auto'),
});
// pk=`TENANT#${tenantId}`, sk=`ALERT_WATCH#${productId}#${variantId|'*'}`
exports.APosInventoryWatchlistEntrySchema = zod_1.z.object({
    tenantId: id_schema_1.TenantIdSchema,
    productId: zod_1.z.string().min(1),
    variantId: zod_1.z.string().optional(), // absent = all variants
    mode: zod_1.z.enum(['include', 'exclude']),
    createdAt: zod_1.z.number().int().positive(),
});
// pk=`TENANT#${tenantId}`, sk=`ALERT_SUPPRESS#${variantId}#${locationId}`
exports.APosInventorySuppressionSchema = zod_1.z.object({
    tenantId: id_schema_1.TenantIdSchema,
    variantId: zod_1.z.string().min(1),
    locationId: zod_1.z.string().min(1),
    dismissedAt: zod_1.z.number().int().positive(),
    onHandAtDismiss: zod_1.z.number(),
    ipAtDismiss: zod_1.z.number(),
    velocityAtDismiss: zod_1.z.number(),
    ttl: zod_1.z.number().int().positive(), // epoch seconds (DynamoDB TTL)
});
// pk=`TENANT#${tenantId}`, sk=`ALERT_SCAN_STATUS`
exports.APosInventoryScanStatusSchema = zod_1.z.object({
    tenantId: id_schema_1.TenantIdSchema,
    state: zod_1.z.enum(['idle', 'running', 'done', 'error']),
    startedAt: zod_1.z.number().int().nonnegative().optional(),
    finishedAt: zod_1.z.number().int().nonnegative().optional(),
    alertsWritten: zod_1.z.number().int().nonnegative().optional(),
    error: zod_1.z.string().optional(),
});
//# sourceMappingURL=inventory-alert.schema.js.map